import { useState } from 'react'
import { Download, Loader2, Paperclip } from 'lucide-react'
import { toast } from 'sonner'

import type { AttachmentLite } from '@/gen/cortado/v1/attachments_pb'
import type { Email } from '@/gen/cortado/v1/emails_pb'
import { useEmailAttachmentDownloadUrl } from '@/stores/emails.store'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog'
import { formatBytes } from '@/lib/file-utils'
import { getErrorDescription } from '@/lib/error-utils'

type PreviewKind = 'pdf' | 'image'

// Matches the types the download route serves inline for captured mail.
const PREVIEWABLE_TYPES: Record<string, PreviewKind> = {
  'application/pdf': 'pdf',
  'image/png': 'image',
  'image/jpeg': 'image',
  'image/gif': 'image',
  'image/webp': 'image',
}

/** The preview a file gets, from its bare media type (Gmail adds parameters). */
export function attachmentPreviewKind(contentType: string): PreviewKind | null {
  const mediaType = contentType.split(';')[0].trim().toLowerCase()
  return PREVIEWABLE_TYPES[mediaType] ?? null
}

/**
 * The same short-lived URL, asking the route to open the file in the browser
 * (inline) or to save it (attachment).
 */
export function withDisposition(url: string, disposition: 'inline' | 'attachment'): string {
  const parsed = new URL(url)
  if (disposition === 'attachment') {
    parsed.searchParams.set('disposition', 'attachment')
  } else {
    parsed.searchParams.delete('disposition')
  }
  return parsed.toString()
}

interface Preview {
  attachment: AttachmentLite
  kind: PreviewKind
  url: string
}

/**
 * The files on one email. PDFs and images open in a viewer with a Download
 * button; anything else downloads. Attachments on mail captured from a
 * connected inbox are fetched from that mailbox on click.
 */
export function EmailAttachmentList({ email }: { email: Email }) {
  const { getDownloadUrl } = useEmailAttachmentDownloadUrl()
  const [openingId, setOpeningId] = useState<string | null>(null)
  const [preview, setPreview] = useState<Preview | null>(null)

  if (email.attachments.length === 0) return null

  const open = async (attachment: AttachmentLite) => {
    setOpeningId(attachment.id)
    try {
      const url = await getDownloadUrl({
        organizationId: email.organizationId,
        emailId: email.id,
        attachmentId: attachment.id,
      })
      const kind = attachmentPreviewKind(attachment.contentType)
      if (kind) {
        setPreview({ attachment, kind, url })
      } else {
        openInNewTab(withDisposition(url, 'attachment'))
      }
    } catch (error) {
      toast.error('Could not open attachment', {
        description: getErrorDescription(error),
      })
    } finally {
      setOpeningId(null)
    }
  }

  return (
    <>
      <ul className="flex flex-wrap gap-2 border-t px-3 py-2" aria-label="Attachments">
        {email.attachments.map((attachment) => (
          <li key={attachment.id}>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-auto max-w-full gap-1.5 rounded-md border bg-background px-2 py-1 text-xs font-normal hover:bg-muted"
              onClick={() => void open(attachment)}
              disabled={openingId === attachment.id}
            >
              {openingId === attachment.id ? (
                <Loader2 className="h-3 w-3 shrink-0 animate-spin" />
              ) : (
                <Paperclip className="h-3 w-3 shrink-0" />
              )}
              <span className="max-w-[180px] truncate">{attachment.filename}</span>
              {attachment.sizeBytes > 0n && (
                <span className="text-muted-foreground">{formatBytes(attachment.sizeBytes)}</span>
              )}
            </Button>
          </li>
        ))}
      </ul>
      <EmailAttachmentPreview preview={preview} onClose={() => setPreview(null)} />
    </>
  )
}

function EmailAttachmentPreview({ preview, onClose }: { preview: Preview | null; onClose: () => void }) {
  if (!preview) return null
  const { attachment, kind, url } = preview
  const viewUrl = withDisposition(url, 'inline')

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90dvh] gap-0 overflow-hidden p-0 sm:max-w-5xl">
        <div className="flex min-w-0 items-start justify-between gap-4 border-b px-6 py-4 pr-12">
          <div className="min-w-0">
            <DialogTitle title={attachment.filename}>
              <span className="block truncate text-sm leading-none font-medium text-foreground">
                {attachment.filename}
              </span>
            </DialogTitle>
            <DialogDescription className="mt-1">
              <span className="block text-xs">
                {attachment.sizeBytes > 0n ? formatBytes(attachment.sizeBytes) : 'Attachment'}
              </span>
            </DialogDescription>
          </div>
          <Button type="button" variant="outline" size="sm" asChild>
            <a href={withDisposition(url, 'attachment')} target="_blank" rel="noopener" download={attachment.filename}>
              <Download className="h-4 w-4" />
              Download
            </a>
          </Button>
        </div>
        <div className="max-h-[78dvh] overflow-auto bg-muted/30">
          {kind === 'pdf' ? (
            <iframe src={viewUrl} title={attachment.filename} className="h-[74dvh] w-full border-0 bg-background" />
          ) : (
            <div className="flex items-center justify-center p-4">
              <img src={viewUrl} alt={attachment.filename} className="max-h-[74dvh] max-w-full object-contain" />
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

function openInNewTab(url: string) {
  const a = document.createElement('a')
  a.href = url
  a.target = '_blank'
  a.rel = 'noopener'
  document.body.appendChild(a)
  a.click()
  a.remove()
}
